import { ofType } from 'redux-observable';
import { mergeMap, map, filter } from 'rxjs';
import { AppEpic, AppState } from 'src/store';
import { of } from 'rxjs';
import {
  fetchFloorsetData,
  fetchConfigurableGridData,
  fetchConfigurableGridConfigs,
  receiveFloorsetData,
  cleanUp,
  refreshConfigurableGridData,
  requestConfigurableGridConfig,
  receiveConfigurableGridConfig,
  requestTopAttributesData,
  fetchConfigurableGridTopAttributes,
  triggerGridRedecorate,
} from './ConfigurableGrid.slice';
import {
  inputIsNotNullOrUndefined,
  isNotSameExternalDataRefreshRequest,
  isScopeDefined,
  topMemInUrl,
  topMemInWorklistSelected,
} from 'src/utils/Functions/epicsFunctions';
import { isNil, isEmpty } from 'lodash';
import { ConfDefnComponentType, type ConfigurableGridComponent } from 'configuration-schemas/confdefnComponents';
import { receiveWorklistConfig, updateSelectedItem } from 'src/pages/Worklist/Worklist.slice';
import {
  receiveScope,
  receiveScopeRefreshTrigger,
} from 'src/components/AssortmentScopeSelector/AssortmentScopeSelector.slice';
import { receiveFilterStateAfterSubmission } from 'src/components/FilterPanel/FilterPanel.slice';
import { setActivePage, setActiveSubPage } from 'src/pages/NavigationShell/NavigationShell.slice';
import type { ConfigurableGridOwnProps } from 'configuration-schemas/ownProps';
import { getViewDefn, getFloorsetData } from './ConfigurableGrid.selectors';
import { ConfigurableGridViewDefn } from 'configuration-schemas/viewdefns/viewdefn';
import {
  setExtraPivotParamsSelection,
  updateConfigureSelections,
  updateTopMemberSelection,
} from '../Subheader/Subheader.slice';
import { extraParamsAvailIfNeeded } from '../Subheader/Subheader.container';
import { maybeGetComponentProps, isSameComponentType } from 'src/utils/Functions/epicsFunctions';
import { LENS_ASST_PLAN_REFRESHING_PAGE, PlanItemStatus } from 'src/pages/AssortmentBuild/Planning.types';
import { planItemUpdate } from 'src/pages/AssortmentBuild/Planning.slice';
import { get } from 'lodash';
import { REQUEST_EXTERNAL_DATA_REFRESH, requestExternalDataRefresh } from '../higherOrder/GlobalDataRefresh';

const filterFloorsetApi = (state: AppState, floorsetApi: ConfigurableGridOwnProps['floorsetApi']): boolean => {
  const viewDefn = getViewDefn(state);
  let floorsetDataAvail = false;
  // check if floorset is needed and exists
  if (!isNil(floorsetApi) || !isNil(viewDefn.subheaderDropdowns.topMember)) {
    floorsetDataAvail = !isEmpty(getFloorsetData(state));
  } else {
    floorsetDataAvail = true;
  }
  return floorsetDataAvail;
};

export const configurableGridConfigLoad: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(requestConfigurableGridConfig.type, setActivePage.type, setActiveSubPage.type, receiveWorklistConfig.type),
    map(() => {
      return maybeGetComponentProps<ConfigurableGridComponent>(state$.value, ConfDefnComponentType.configurableGrid);
    }),
    filter(inputIsNotNullOrUndefined),
    mergeMap(({ configApi }) => {
      if (isSameComponentType<ConfigurableGridComponent>(state$.value, ConfDefnComponentType.configurableGrid)) {
        return of(cleanUp(), fetchConfigurableGridConfigs(configApi));
      }
      return of(fetchConfigurableGridConfigs(configApi));
    }),
  );
};

export const configurableGridFloorsetLoad: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(
      receiveScope.type,
      receiveScopeRefreshTrigger.type,
      receiveFilterStateAfterSubmission.type,
      receiveConfigurableGridConfig.type,
      LENS_ASST_PLAN_REFRESHING_PAGE,
    ),
    map(() => {
      return maybeGetComponentProps<ConfigurableGridComponent>(state$.value, ConfDefnComponentType.configurableGrid);
    }),
    filter(inputIsNotNullOrUndefined),
    filter(() => isScopeDefined(state$.value.scope)),
    // we need this since we actually care about view defn now for override
    filter(() => !isNil(getViewDefn(state$.value))),
    mergeMap(({ floorsetApi }) => {
      // Override the componentProps "floorsetApi" with viewDefn's topMember dropdown when it is present
      const viewDefn: ConfigurableGridViewDefn = getViewDefn(state$.value);
      if (!isNil(viewDefn.subheaderDropdowns.topMember)) {
        return of(fetchFloorsetData(viewDefn.subheaderDropdowns.topMember.dataApi));
      } else {
        return of(fetchFloorsetData(floorsetApi));
      }
    }),
  );
};

/**
 * This is separated out into it's own epic because it is dependent on the floorset data in the configurableGridFloorsetLoad epic.
 * It will be triggered for initial page load and scope/filter changes whenever the floorset data is loaded.
 *
 */
export const configurableGridLoad: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(
      updateSelectedItem.type,
      receiveFloorsetData.type,
      updateTopMemberSelection.type,
      refreshConfigurableGridData.type,
      setExtraPivotParamsSelection.type,
      receiveScope.type,
      updateConfigureSelections.type,
    ),
    // only load on configure change if viewdefn says to
    filter((action) => {
      const viewDefn = getViewDefn(state$.value);
      const shouldRefreshOnAggChange =
        !isNil(viewDefn) && (viewDefn as ConfigurableGridViewDefn).configure?.main?.includeAggBys == true;
      return action?.type == updateConfigureSelections.type ? shouldRefreshOnAggChange : true;
    }),
    map(() => {
      return maybeGetComponentProps<ConfigurableGridComponent>(state$.value, ConfDefnComponentType.configurableGrid);
    }),
    filter(inputIsNotNullOrUndefined),
    filter(() => !isNil(getViewDefn(state$.value))),
    filter(() => topMemInWorklistSelected(state$.value) || topMemInUrl() != null),
    filter(({ floorsetApi }) => filterFloorsetApi(state$.value, floorsetApi)),
    filter(() => extraParamsAvailIfNeeded(state$.value)),
    filter(() => isScopeDefined(state$.value.scope)),
    mergeMap((ownProps) => {
      if (isSameComponentType<ConfigurableGridComponent>(state$.value, ConfDefnComponentType.configurableGrid)) {
        return of(cleanUp(), fetchConfigurableGridData(ownProps as ConfigurableGridOwnProps));
      }

      return of(fetchConfigurableGridData(ownProps as ConfigurableGridOwnProps));
    }),
  );
};

export const configurableGridTopAttributesLoad: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(requestTopAttributesData.type),
    map(() => {
      return maybeGetComponentProps<ConfigurableGridComponent>(state$.value, ConfDefnComponentType.configurableGrid);
    }),
    filter(inputIsNotNullOrUndefined),
    filter(() => !isNil(getViewDefn(state$.value))),
    filter(() => topMemInWorklistSelected(state$.value)),
    filter(({ floorsetApi }) => filterFloorsetApi(state$.value, floorsetApi)),
    filter(() => extraParamsAvailIfNeeded(state$.value)),
    mergeMap((ownProps) => {
      return of(fetchConfigurableGridTopAttributes(ownProps as ConfigurableGridOwnProps));
    }),
  );
};

/**
 * Bridges a external redecorate requests from external edits into a grid redecorate.
 * `HistoryStylePane` can be open and editable When and it emits
 * `notifyHostViewsToRedecorate` with the affected stylecolors.
 * If a `ConfigurableGrid` is the active view we forward that to `triggerGridRedecorate`,
 * which `EditableGrid` picks up (via a drilled prop) and uses to redecorate the matching rows.
 */
export const configurableGridExternalRedecorate: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(REQUEST_EXTERNAL_DATA_REFRESH),
    filter((action) => isNotSameExternalDataRefreshRequest(action, ConfDefnComponentType.configurableGrid)),
    filter(
      () => !!maybeGetComponentProps<ConfigurableGridComponent>(state$.value, ConfDefnComponentType.configurableGrid),
    ),
    filter(() => !isNil(getViewDefn(state$.value))),
    map(({ payload }: ReturnType<typeof requestExternalDataRefresh>) =>
      triggerGridRedecorate({ ...payload, editTime: Date.now() }),
    ),
  );
};

/** This exists to reload current ConfigurableGrid's macros when plan completes if configured to */
export const configurableGridPlanCompleteTopAttributesLoad: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(planItemUpdate.type),
    map(() => {
      // don't run it if we're not on a configgrid
      return maybeGetComponentProps<ConfigurableGridComponent>(state$.value, ConfDefnComponentType.configurableGrid);
    }),
    filter(inputIsNotNullOrUndefined),
    filter(() => {
      // check for the config option and skip if not `planCompleteOnly`
      const viewDefn = getViewDefn(state$.value);
      return !isNil(viewDefn) && get(viewDefn, 'topAttributes.refreshOn') === 'planCompleteOnly';
    }),
    filter(() => {
      // if the above checks out see what the plan status is
      const planTracker = state$.value.planTracker;
      // detect askForRefresh transition from false to true
      // this is probably the trickiest part and most open to adjustment
      // right now it copies logic from `Planning.slice` for setting the refresh icon
      // but could be adjusted to refresh in more scenarios
      return (
        planTracker.assortmentPlanInProgress == false &&
        planTracker.planItems.filter((p) => p.status === PlanItemStatus.Pending).length == 0 &&
        planTracker.askForRefresh
      );
    }),
    filter(() => topMemInWorklistSelected(state$.value)),
    filter(({ floorsetApi }) => filterFloorsetApi(state$.value, floorsetApi)),
    filter(() => extraParamsAvailIfNeeded(state$.value)),
    mergeMap(() => of(requestTopAttributesData())),
  );
};
